import { supabase } from '../lib/supabase';

// Callers pass the current association's id; there is no Crestview default.
const requireHoaId = (hoaId) => {
    if (!hoaId) throw new Error('queryOverrides: hoaId is required');
    return hoaId;
};

// Fuzzy match: what % of query words appear in override trigger?
const fuzzyMatch = (query, triggerPhrase) => {
    const queryWords = query.toLowerCase()
        .match(/\b[a-z]{3,}\b/g) || [];
    const triggerWords = triggerPhrase.toLowerCase()
        .match(/\b[a-z]{3,}\b/g) || [];
    if (triggerWords.length === 0) return 0;
    const matches = queryWords
        .filter(w => triggerWords.includes(w)).length;
    return matches / triggerWords.length;
};

export const queryOverrides = {

    // Check if a query has an admin override (reads from Supabase)
    // Returns override object or null — async
    checkOverride: async (queryText, hoaId) => {
        try {
            const { data, error } = await supabase
                .from('rag_overrides')
                .select('*')
                .eq('hoa_id', requireHoaId(hoaId))
                .eq('active', true);

            if (error || !data) return null;

            let bestMatch = null;
            let bestScore = 0;
            for (const override of data) {
                const score = fuzzyMatch(queryText, override.trigger_phrase);
                if (score > 0.6 && score > bestScore) {
                    bestScore = score;
                    bestMatch = override;
                }
            }
            return bestMatch ? { ...bestMatch, match_score: bestScore } : null;
        } catch {
            return null;
        }
    },

    // Save a new override or update existing (Supabase)
    saveOverride: async (triggerPhrase, correctAnswer, adminId, originalQuery = null, hoaId) => {
        const existing = await queryOverrides.getAll(hoaId);
        const match = existing.find(o => fuzzyMatch(triggerPhrase, o.trigger_phrase) > 0.8);

        const entry = {
            id: match ? match.id : `override_${Date.now()}`,
            hoa_id: requireHoaId(hoaId),
            trigger_phrase: triggerPhrase,
            correct_answer: correctAnswer,
            original_query: originalQuery,
            created_by: adminId,
            active: true,
            use_count: match ? (match.use_count || 0) : 0,
            updated_at: new Date().toISOString(),
            ...(match ? {} : { created_at: new Date().toISOString() }),
        };

        const { data, error } = await supabase
            .from('rag_overrides')
            .upsert(entry, { onConflict: 'id' })
            .select()
            .single();

        if (error) throw error;
        return data;
    },

    // Increment use count when override is served
    recordUsage: async (overrideId) => {
        const { data: current } = await supabase
            .from('rag_overrides')
            .select('use_count')
            .eq('id', overrideId)
            .single();

        await supabase
            .from('rag_overrides')
            .update({
                use_count: (current?.use_count || 0) + 1,
                last_used: new Date().toISOString(),
            })
            .eq('id', overrideId);
    },

    // Toggle override active/inactive
    toggleActive: async (overrideId) => {
        const { data: current } = await supabase
            .from('rag_overrides')
            .select('active')
            .eq('id', overrideId)
            .single();

        await supabase
            .from('rag_overrides')
            .update({ active: !current?.active, updated_at: new Date().toISOString() })
            .eq('id', overrideId);
    },

    // Delete override
    deleteOverride: async (overrideId) => {
        await supabase
            .from('rag_overrides')
            .delete()
            .eq('id', overrideId);
    },

    // Get all overrides for this HOA
    getAll: async (hoaId) => {
        try {
            const { data, error } = await supabase
                .from('rag_overrides')
                .select('*')
                .eq('hoa_id', requireHoaId(hoaId))
                .order('updated_at', { ascending: false });

            if (error) return [];
            return data || [];
        } catch { return []; }
    },

    // Log every query to Supabase rag_query_log
    logQuery: async (queryText, answer, userId, userRole,
        overrideUsed = null, litigationResult = null, threadId = null,
        tokenUsage = null, hoaId) => {
        try {
            const inputTokens = tokenUsage?.input_tokens || 0;
            const outputTokens = tokenUsage?.output_tokens || 0;
            const estimatedCost = (inputTokens * 0.000001) + (outputTokens * 0.000005);

            const entry = {
                id: `query_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
                hoa_id: requireHoaId(hoaId),
                thread_id: threadId,
                query: queryText,
                answer: answer,
                user_id: userId && userId !== 'anonymous' ? userId : null,
                user_role: userRole,
                override_used: overrideUsed || null,
                has_override: !!overrideUsed,
                litigation_flag: litigationResult?.flagged || false,
                litigation_risk: litigationResult?.risk_level || 'low',
                litigation_category: litigationResult?.category || null,
                input_tokens: inputTokens,
                output_tokens: outputTokens,
                estimated_cost_usd: estimatedCost,
                timestamp: new Date().toISOString(),
            };

            const { data, error } = await supabase
                .from('rag_query_log')
                .insert(entry)
                .select()
                .single();

            if (error) {
                console.warn('Query log insert error:', error.message);
                return null;
            }
            return data;
        } catch (err) {
            console.warn('Query log error:', err.message);
            return null;
        }
    },

    // Get query log for this HOA (newest first)
    getQueryLog: async (limit = 500, hoaId) => {
        try {
            const { data, error } = await supabase
                .from('rag_query_log')
                .select('*')
                .eq('hoa_id', requireHoaId(hoaId))
                .order('timestamp', { ascending: false })
                .limit(limit);

            if (error) return [];
            return data || [];
        } catch { return []; }
    },

    // Number of AI questions per asker (user_id -> count) for the People view.
    // Only called from the admin People view (RLS limits rag_query_log reads to level >= 50).
    getQuestionCountsByUser: async (hoaId) => {
        try {
            const { data, error } = await supabase
                .from('rag_query_log')
                .select('user_id')
                .eq('hoa_id', requireHoaId(hoaId))
                .not('user_id', 'is', null)
                .limit(5000);
            if (error) return new Map();
            const counts = new Map();
            (data || []).forEach(row => counts.set(row.user_id, (counts.get(row.user_id) || 0) + 1));
            return counts;
        } catch { return new Map(); }
    },
};
